import { FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CustomModal } from "../../../../components/CustomModal/CustomModal";
import { CustomTextInput } from "../../../../components/CustomTextInput/CustomTextInput";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { useUpdateUserEmployee } from "../../../../api/employee";
import {
  getCurrentFleetId,
  patchCurrentUser,
} from "../../../../utils/getCurrentFleet";
import useStyles from "./styles";

export type ProfileValues = {
  firstname: string;
  lastname: string;
  email: string;
};

type Props = {
  open: boolean;
  user: TUser | null;
  onClose: () => void;
  onUpdated?: (values: ProfileValues) => void;
};

export const ModalUpdateProfile: FC<Props> = ({
  open,
  user,
  onClose,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const submittedRef = useRef<ProfileValues | null>(null);

  const applyUpdate = () => {
    const values = submittedRef.current;
    if (!values) return;
    patchCurrentUser(values);
    onUpdated && onUpdated(values);
  };

  const { mutate: updateProfile, isPending } = useUpdateUserEmployee(
    getCurrentFleetId(),
    user?.id ?? 0,
    onClose,
    applyUpdate
  );

  const validationSchema = Yup.object({
    lastname: Yup.string()
      .trim()
      .required(t("required.lastname", "Last name is required")),
    firstname: Yup.string()
      .trim()
      .required(t("required.firstname", "First name is required")),
    email: Yup.string()
      .trim()
      .email(t("required.emailInvalid", "Email is invalid"))
      .required(t("required.email", "Email is required")),
  });

  const formik = useFormik<ProfileValues>({
    initialValues: {
      firstname: user?.firstname ?? "",
      lastname: user?.lastname ?? "",
      email: user?.email ?? "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const trimmed: ProfileValues = {
        firstname: values.firstname.trim(),
        lastname: values.lastname.trim(),
        email: values.email.trim(),
      };
      submittedRef.current = trimmed;
      updateProfile(trimmed);
    },
  });

  // Reset the form each time the modal is (re)opened.
  useEffect(() => {
    if (open) formik.resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id]);

  return (
    <CustomModal
      open={open}
      title={t("setting.editProfile", "Edit profile")}
      onClose={onClose}
      onCancel={onClose}
      onSave={() => formik.handleSubmit()}
      loadingSave={isPending}
      disabledSaveButton={!formik.dirty || isPending}
    >
      <div className={classes.containerText}>
        <div>
          <CustomTextInput
            label={t("collaborator.name", "Name")}
            placeholder={t("collaborator.name", "Name")}
            marginBottom={"0px"}
            name="lastname"
            value={formik.values.lastname}
            onChange={formik.handleChange}
          />
          {formik.touched.lastname && formik.errors.lastname && (
            <CustomTypography
              text={formik.errors.lastname}
              variant="link"
              styles={{ containerText: classes.errorText }}
            />
          )}
        </div>
        <div>
          <CustomTextInput
            label={t("collaborator.firstName", "Firstname")}
            placeholder={t("collaborator.firstName", "Firstname")}
            marginBottom={"0px"}
            name="firstname"
            value={formik.values.firstname}
            onChange={formik.handleChange}
          />
          {formik.touched.firstname && formik.errors.firstname && (
            <CustomTypography
              text={formik.errors.firstname}
              variant="link"
              styles={{ containerText: classes.errorText }}
            />
          )}
        </div>
        <div>
          <CustomTextInput
            label={t("collaborator.email", "Email")}
            placeholder={t("collaborator.email", "Email")}
            marginBottom={"0px"}
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
          />
          {formik.touched.email && formik.errors.email && (
            <CustomTypography
              text={formik.errors.email}
              variant="link"
              styles={{ containerText: classes.errorText }}
            />
          )}
        </div>
      </div>
    </CustomModal>
  );
};
